import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/client';

export interface QueryState<T> {
  data: T | undefined;
  error: ApiError | null;
  loading: boolean;
  /** Re-runs the query (e.g. after a mutation or a retry click). */
  reload: () => void;
}

export interface QueryOptions {
  /**
   * While the result for a NEW key loads, keep returning the previous key's data (default true:
   * pagers and reload keys keep the old rows on screen instead of flashing a skeleton). Pass false
   * when stale data would be wrong rather than merely old - e.g. another school's time zone.
   * A null key never returns data either way.
   */
  keepPreviousData?: boolean;
}

/**
 * Minimal data-fetching hook: runs `fetcher` whenever `key` changes, aborts stale requests, and
 * exposes loading/error/data. `key` must capture every input of the fetcher. A null key disables
 * the query and clears its data.
 */
export function useApiQuery<T>(
  key: string | null,
  fetcher: (signal: AbortSignal) => Promise<T>,
  options: QueryOptions = {},
): QueryState<T> {
  const keepPrevious = options.keepPreviousData ?? true;
  // The data remembers which key it was fetched for, so stale results can be withheld.
  const [result, setResult] = useState<{ key: string; data: T } | undefined>(undefined);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(key !== null);
  const [nonce, setNonce] = useState(0);
  const fetcherRef = useRef(fetcher);

  useEffect(() => {
    fetcherRef.current = fetcher;
  });

  useEffect(() => {
    if (key === null) {
      // Nothing to load for a disabled query (and nothing stale to show).
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLoading(false);
      setError(null);
      return;
    }
    const controller = new AbortController();
    let active = true;
    // Loading state intentionally flips at the start of every fetch cycle.
    setLoading(true);
    setError(null);
    fetcherRef
      .current(controller.signal)
      .then((data) => {
        if (active) setResult({ key, data });
      })
      .catch((err: unknown) => {
        if (!active || (err as Error).name === 'AbortError') return;
        setError(
          err instanceof ApiError
            ? err
            : new ApiError(0, 'UNKNOWN', 'An unexpected error occurred.', null),
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [key, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const data =
    key === null || result === undefined
      ? undefined
      : result.key === key || keepPrevious
        ? result.data
        : undefined;
  return { data, error, loading, reload };
}
